"use client";

import { useState } from "react";
import {
  Button,
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  CircularProgress,
} from "@mui/material";

export default function PaymentStep({
  amount,
  prev,
  participationType,
  setStep,
  onPayClick,
  userData,
  onApplyCoupon,
}) {
  const [coupon, setCoupon] = useState("");
  const [discountedAmount, setDiscountedAmount] = useState(amount);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleBack = () => {
    if (participationType === "spectator") {
      setStep(1);
    } else {
      prev();
    }
  };

  const handleApplyCoupon = () => {
    let discount = 0;
    if (coupon.toUpperCase() === "YTP50") discount = 0.5;
    if (coupon.toUpperCase() === "YTP100") discount = 1;

    if (discount > 0) {
      let newAmount = amount - amount * discount;
      newAmount = Math.round(Math.max(newAmount, 0)); // round & avoid negative
      setDiscountedAmount(newAmount);
      setAppliedCoupon(coupon.toUpperCase());
      setError(null); // clear error
      onApplyCoupon(newAmount, coupon.toUpperCase());
    } else {
      setDiscountedAmount(amount);
      setAppliedCoupon(null);
      setError("Invalid coupon code"); // show error
      onApplyCoupon(amount, null);
    }
  };

  const handlePayClick = async () => {
    setLoading(true); // ✅ show loader
    try {
      await onPayClick(discountedAmount, appliedCoupon);
    } finally {
      setLoading(false); // ✅ hide loader once done
    }
  };

  return (
    <Box
      sx={{
        maxWidth: 650,
        mx: "auto",
        display: "flex",
        flexDirection: "column",
        gap: 3,
        textAlign: "center",
        borderRadius: "10px",
        boxShadow: "1px 1px 1px 1px rgba(0,0,0,0.1)",
        backgroundColor: "white",
      }}
    >
      <span className="block py-1 text-center text-base rounded-t-lg font-bold bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white-1">
        6th Tour De Sanawar 2025
      </span>

      <div className="w-full h-full p-6 pt-0">
        {/* Header */}
        <Box sx={{ paddingBottom: 2 }}>
          <Typography variant="h4" fontWeight="bold" color="text.primary">
            Payment
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Review your details and complete the payment to finish registration.
          </Typography>
        </Box>

        {/* User Details */}
        <Paper sx={{ p: 2, textAlign: "left", bgcolor: "grey.100" }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Your Details
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Typography fontWeight="medium">Name</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography>
                {userData.firstName} {userData.lastName || ""}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography fontWeight="medium">Email</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography>{userData.email}</Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography fontWeight="medium">Contact</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography>{userData.primaryContact}</Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography fontWeight="medium">Participant Type</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography sx={{ textTransform: "capitalize" }}>
                {userData.participationType}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Coupon Input */}
        <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
          <TextField
            label="Coupon Code"
            value={coupon}
            onChange={(e) => {
              // Normalize input: uppercase + trim spaces
              const value = e.target.value.toUpperCase().replace(/\s+/g, "");
              setCoupon(value);
              if (error) setError(null); // clear error as they type
            }}
            error={!!error}
            helperText={error || ""}
            fullWidth
          />

          <Button
            variant="outlined"
            sx={{
              height: "55px",
              backgroundColor: "var(--color-blue)",
              color: "white",
              borderColor: "var(--color-blue)",
              "&:hover": {
                backgroundColor: "var(--color-blue)",
                color: "white",
                borderColor: "var(--color-blue)",
              },
            }}
            onClick={handleApplyCoupon}
          >
            Apply
          </Button>
        </Box>

        {appliedCoupon && !error && (
          <Typography
            variant="body2"
            sx={{ mt: 1, textAlign: "left" }}
            color="success.main"
            fontWeight="bold"
          >
            Coupon "{appliedCoupon}" applied!
          </Typography>
        )}

        {/* Amount Summary */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: "center",
            mt: 2,
          }}
        >
          <Typography variant="body1" fontWeight="medium" color="text.primary">
            Total Amount
          </Typography>
          <Typography
            variant="h5"
            fontWeight="bold"
            color={discountedAmount === 0 ? "success.main" : "primary"}
            sx={{ mt: { xs: 1, sm: 0, color:"var(--color-blue)" } }}
          >
            ₹{discountedAmount.toLocaleString("en-IN")}
          </Typography>
        </Box>

        {/* Buttons */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mt: 3,
            gap: 2,
          }}
        >
          <Button
            variant="outlined"
            onClick={handleBack}
            sx={{
              borderColor: "var(--color-blue)",
              color: "var(--color-blue)",
              "&:hover": {
                borderColor: "var(--color-blue-hover)",
                color: "var(--color-blue-hover)",
              },
            }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            color="success"
            disabled={loading}
            onClick={handlePayClick}
            sx={{ minWidth: 200 }}
          >
            {loading ? (
              <CircularProgress size={24} sx={{ color: "white" }} />
            ) : discountedAmount === 0 ? (
              "Complete Registration"
            ) : (
              "Pay & Complete Registration"
            )}
          </Button>
        </Box>
      </div>
    </Box>
  );
}
